import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import SEO from '../components/SEO';
import './PricingPage.css';

interface PricingTier {
  id: string;
  name: string;
  price: number;
  priceId: string;
  maxProjects: number;
  maxUsers: number;
  features: string[];
}

const PricingPage: React.FC = () => {
  const [tiers, setTiers] = useState<PricingTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTier, setSelectedTier] = useState<string | null>(null);
  const [processingCheckout, setProcessingCheckout] = useState(false);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const navigate = useNavigate();
  const { user } = useAuth();

  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

  // Calculate annual pricing (20% discount on 12 months)
  const getPrice = (monthlyPrice: number) => {
    if (billingCycle === 'annual') {
      return Math.round(monthlyPrice * 12 * 0.8); // 20% off yearly total
    }
    return monthlyPrice;
  };

  const getMonthlyEquivalent = (monthlyPrice: number) => {
    return Math.round(monthlyPrice * 0.8); // Monthly rate when billed annually
  };

  const getSavings = (monthlyPrice: number) => {
    return Math.round(monthlyPrice * 12 * 0.2); // Annual savings
  };

  useEffect(() => {
    fetchTiers();

    // Check if user just logged in and had a pending tier selection
    const pendingTier = localStorage.getItem('pending_tier_selection');
    const pendingBilling = localStorage.getItem('pending_billing_cycle');

    if (user && pendingTier) {
      console.log('✅ User logged in, resuming tier selection:', pendingTier);
      // Clear the pending items
      localStorage.removeItem('pending_tier_selection');
      localStorage.removeItem('pending_billing_cycle');

      // Set billing cycle if it was saved
      if (pendingBilling) {
        setBillingCycle(pendingBilling as 'monthly' | 'annual');
      }

      // Auto-trigger the plan selection
      setTimeout(() => {
        handleSelectPlan(pendingTier);
      }, 500);
    }
  }, [user]);

  const fetchTiers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/payments/tiers`);
      const data = await response.json();

      if (data.success) {
        setTiers(data.data);
      } else {
        // Fallback to hardcoded tiers if API fails
        setTiers(DEFAULT_TIERS);
      }
    } catch (error) {
      console.error('Error fetching tiers:', error);
      // Use hardcoded tiers as fallback
      setTiers(DEFAULT_TIERS);
    } finally {
      setLoading(false);
    }
  };

  // All features available in ALL tiers
  const ALL_FEATURES = [
    'AI-Powered Site Compliance & GIS Analysis - Live county data for verified setbacks & zoning',
    'AI-Powered Blueprint Processing - AI wall & room detection from any plan',
    '3D Floor Plans & Site Visualization - See your project before you build',
    'Smart Vendor Bidding - Send requests, track responses, compare quotes',
    'AI-Powered Vendor Communications - AI handles texts, emails & calls for you',
    'Real-Time Team Collaboration - Keep your crew and subs in sync',
    'Construction Phase Tracking - Monitor progress from foundation to finish',
    'Smart Document Management - All plans, permits & contracts organized',
    'AI-Powered Scheduling System - AI vendor scheduling and follow-ups',
    'Email Integration - Gmail & Outlook with smart inbox management',
    'Mobile Access - Run your jobs from the field',
    'AI-Powered Cost Tracking & Budget Alerts - Real-time spending analysis',
    'Unlimited Project Archive - Keep every job for future reference'
  ];

  // Default tier configuration
  const DEFAULT_TIERS: PricingTier[] = [
    {
      id: 'pilot',
      name: 'Pilot',
      price: 149,
      priceId: 'price_pilot',
      maxProjects: 1,
      maxUsers: 1,
      features: ALL_FEATURES
    },
    {
      id: 'starter',
      name: 'Starter',
      price: 299,
      priceId: 'price_starter',
      maxProjects: 2,
      maxUsers: 2,
      features: ALL_FEATURES
    },
    {
      id: 'professional',
      name: 'Professional',
      price: 599,
      priceId: 'price_professional',
      maxProjects: 6,
      maxUsers: 6,
      features: ALL_FEATURES
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 999,
      priceId: 'price_enterprise',
      maxProjects: -1,
      maxUsers: -1,
      features: ALL_FEATURES
    }
  ];

  const handleSelectPlan = async (tierId: string) => {
    // IMPORTANT: User must be logged in before payment
    if (!user) {
      console.log('⚠️ User not logged in, redirecting to signup');
      // Save the selected tier to localStorage so we can resume after signup
      localStorage.setItem('pending_tier_selection', tierId);
      localStorage.setItem('pending_billing_cycle', billingCycle);
      // Redirect to login page, which will redirect back here after login
      navigate('/login?redirect=/pricing');
      return;
    }

    setSelectedTier(tierId);
    setProcessingCheckout(true);

    try {
      const response = await fetch(`${API_URL}/api/payments/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tier: tierId,
          billingCycle: billingCycle,
          email: user?.email || null,
          userId: user?.id || null, // Now guaranteed to exist
        }),
      });

      const data = await response.json();

      if (data.success && data.data.url) {
        // Redirect to Stripe Checkout
        window.location.href = data.data.url;
      } else {
        alert('Failed to create checkout session. Please try again.');
        setProcessingCheckout(false);
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      alert('An error occurred. Please try again.');
      setProcessingCheckout(false);
    }
  };

  if (loading) {
    return (
      <div className="pricing-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading pricing plans...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="Pricing - Construction Management Software Plans | HomeQuest Tech"
        description="Choose the perfect plan for your construction business. All plans include AI-powered site compliance, automated communications, smart vendor bidding, and more. Start your 2-week free trial. Lock in 30% OFF beta pricing for life!"
        keywords="construction software pricing, project management pricing, contractor software cost, construction platform plans, AI construction software pricing, beta pricing construction software"
        canonical="/pricing"
      />
      {/* URGENT BETA BANNER - FIXED AT VERY TOP */}
      <div style={{
        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
        padding: '20px 24px',
        textAlign: 'center' as const,
        color: 'white',
        fontWeight: 'bold',
        fontSize: '20px',
        borderBottom: '4px solid #991b1b',
        position: 'fixed' as const,
        top: 0,
        left: 0,
        right: 0,
        width: '100%',
        zIndex: 9999,
        boxShadow: '0 6px 16px rgba(0,0,0,0.25)'
      }}>
        🔥 LIMITED BETA OFFER: Lock in 30% OFF FOR LIFE • Only 3 Founding Member Spots Left •
        <span style={{
          background: 'white',
          color: '#dc2626',
          padding: '6px 16px',
          borderRadius: '6px',
          marginLeft: '16px',
          fontWeight: 'bold',
          fontSize: '18px'
        }}>ENDS SOON</span>
      </div>

      <div className="pricing-page" style={{ marginTop: '72px' }}>
      <div className="pricing-hero">
        <div className="pricing-badge-top">🏗️ Professional Construction Management</div>
        <h1 className="pricing-title">Choose Your Plan</h1>
        <p className="pricing-subtitle">
          Transform your construction workflow with AI-powered intelligence. All plans include our complete feature set.
        </p>

        {/* Billing Cycle Toggle */}
        <div className="billing-toggle-container">
          <div className="billing-toggle">
            <button
              className={`billing-option ${billingCycle === 'monthly' ? 'active' : ''}`}
              onClick={() => setBillingCycle('monthly')}
            >
              Monthly
            </button>
            <button
              className={`billing-option ${billingCycle === 'annual' ? 'active' : ''}`}
              onClick={() => setBillingCycle('annual')}
            >
              Annual
              <span className="save-badge">Save 20%</span>
            </button>
          </div>
          <p className="billing-note">
            {billingCycle === 'annual' ? '💰 Save up to $2,400/year with annual billing' : '📅 Pay monthly, cancel anytime'}
          </p>
        </div>

        <div className="pricing-trust-badges">
          <div className="trust-badge">
            <svg className="trust-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>2-Week Free Trial</span>
          </div>
          <div className="trust-badge">
            <svg className="trust-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <span>SSL Secured</span>
          </div>
          <div className="trust-badge">
            <svg className="trust-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <span>Cancel Anytime</span>
          </div>
        </div>
      </div>

      <div className="pricing-grid">
        {tiers.map((tier, index) => (
          <div
            key={tier.id}
            className={`pricing-card ${
              tier.id === 'professional' ? 'pricing-card-featured' : ''
            }`}
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            {tier.id === 'professional' && (
              <div className="pricing-badge">⭐ Most Popular</div>
            )}

            <div className="trial-badge">
              <svg className="trial-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              2-Week Free Trial
            </div>

            <div className="pricing-card-header">
              <div className="pricing-tier-icon">
                {tier.id === 'pilot' && '✈️'}
                {tier.id === 'starter' && '🚀'}
                {tier.id === 'professional' && '💼'}
                {tier.id === 'enterprise' && '🏢'}
              </div>
              <h3 className="pricing-card-title">{tier.name}</h3>

              {billingCycle === 'annual' && (
                <div className="savings-badge">
                  Save ${getSavings(tier.price)}/year
                </div>
              )}

              <div className="pricing-card-price">
                <span className="pricing-currency">$</span>
                <span className="pricing-amount">{getPrice(tier.price)}</span>
                <span className="pricing-period">
                  {billingCycle === 'monthly' ? '/mo' : '/year'}
                </span>
              </div>

              {billingCycle === 'annual' && (
                <div className="annual-total">
                  ${getMonthlyEquivalent(tier.price)}/mo • billed annually
                </div>
              )}

              {billingCycle === 'monthly' && tier.price > 0 && (
                <div className="or-save">
                  or ${getMonthlyEquivalent(tier.price)}/mo billed annually
                </div>
              )}

              <div className="pricing-card-limits">
                <div className="limit-badge">
                  <svg className="limit-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  {tier.maxProjects === -1 ? '∞' : tier.maxProjects} Projects
                </div>
                <div className="limit-badge">
                  <svg className="limit-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                  {tier.maxUsers === -1 ? '∞' : tier.maxUsers} Users
                </div>
              </div>
              <p className="pricing-card-description">
                {tier.id === 'pilot' && 'Perfect for solo builders testing the waters'}
                {tier.id === 'starter' && 'Perfect for small teams getting started'}
                {tier.id === 'professional' && 'Ideal for growing construction businesses'}
                {tier.id === 'enterprise' && 'For large-scale operations and teams'}
              </p>
            </div>

            <ul className="pricing-features">
              {tier.features.slice(0, 8).map((feature, idx) => (
                <li key={idx} className="pricing-feature">
                  <svg
                    className="pricing-feature-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  {feature}
                </li>
              ))}
              {tier.features.length > 8 && (
                <li className="pricing-feature-more">
                  +{tier.features.length - 8} more features
                </li>
              )}
            </ul>

            <button
              className={`pricing-button ${
                tier.id === 'professional' ? 'pricing-button-featured' : ''
              }`}
              onClick={() => handleSelectPlan(tier.id)}
              disabled={processingCheckout && selectedTier === tier.id}
            >
              {processingCheckout && selectedTier === tier.id ? (
                <>
                  <span className="button-spinner"></span>
                  Processing...
                </>
              ) : (
                <>
                  Start Free Trial
                  <svg className="button-arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </>
              )}
            </button>
            <p className="trial-terms">
              {billingCycle === 'monthly'
                ? `Free for 2 weeks, then $${tier.price}/mo`
                : `Free for 2 weeks, then $${getPrice(tier.price)}/year`
              }
            </p>
          </div>
        ))}
      </div>

      <div className="pricing-guarantee">
        <div className="guarantee-icon">✓</div>
        <h3>30-Day Money-Back Guarantee</h3>
        <p>Not satisfied? Get a full refund within the first 30 days, no questions asked.</p>
      </div>

      {/* Feature Comparison Table */}
      <div className="comparison-section">
        <h2 className="comparison-title">Compare Plans</h2>
        <p className="comparison-subtitle">Detailed feature breakdown to help you choose the right plan</p>

        <div className="comparison-table-wrapper">
          <table className="comparison-table">
            <thead>
              <tr>
                <th className="feature-column">Features</th>
                <th className="plan-column">
                  <div className="plan-header">
                    <div className="plan-icon">✈️</div>
                    <div className="plan-name">Pilot</div>
                  </div>
                </th>
                <th className="plan-column">
                  <div className="plan-header">
                    <div className="plan-icon">🚀</div>
                    <div className="plan-name">Starter</div>
                  </div>
                </th>
                <th className="plan-column featured-column">
                  <div className="plan-header">
                    <div className="plan-icon">💼</div>
                    <div className="plan-name">Professional</div>
                    <span className="popular-tag">Most Popular</span>
                  </div>
                </th>
                <th className="plan-column">
                  <div className="plan-header">
                    <div className="plan-icon">🏢</div>
                    <div className="plan-name">Enterprise</div>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr className="category-row">
                <td colSpan={5}>Project Management</td>
              </tr>
              <tr>
                <td className="feature-name">Active Projects</td>
                <td>1</td>
                <td>2</td>
                <td className="featured-column">6</td>
                <td>Unlimited</td>
              </tr>
              <tr>
                <td className="feature-name">Team Members</td>
                <td>1</td>
                <td>2</td>
                <td className="featured-column">6</td>
                <td>Unlimited</td>
              </tr>
              <tr>
                <td className="feature-name">Project Archive</td>
                <td><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
                <td className="featured-column"><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
              </tr>

              <tr className="category-row">
                <td colSpan={5}>AI-Powered Features</td>
              </tr>
              <tr>
                <td className="feature-name">AI-Powered Blueprint Processing</td>
                <td><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
                <td className="featured-column"><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
              </tr>
              <tr>
                <td className="feature-name">Auto Wall & Room Detection</td>
                <td><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
                <td className="featured-column"><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
              </tr>
              <tr>
                <td className="feature-name">AI-Powered Phone System</td>
                <td><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
                <td className="featured-column"><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
              </tr>

              <tr className="category-row">
                <td colSpan={5}>Visualization & Planning</td>
              </tr>
              <tr>
                <td className="feature-name">3D Floor Plans</td>
                <td><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
                <td className="featured-column"><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
              </tr>
              <tr>
                <td className="feature-name">Site Visualization</td>
                <td><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
                <td className="featured-column"><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
              </tr>
              <tr>
                <td className="feature-name">GIS & Terrain Analysis</td>
                <td><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
                <td className="featured-column"><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
              </tr>

              <tr className="category-row">
                <td colSpan={5}>Vendor Management</td>
              </tr>
              <tr>
                <td className="feature-name">Smart Vendor Bidding</td>
                <td><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
                <td className="featured-column"><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
              </tr>
              <tr>
                <td className="feature-name">Auto Vendor Messaging</td>
                <td><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
                <td className="featured-column"><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
              </tr>

              <tr className="category-row">
                <td colSpan={5}>Support & Training</td>
              </tr>
              <tr>
                <td className="feature-name">Email Support</td>
                <td><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
                <td className="featured-column"><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
              </tr>
              <tr>
                <td className="feature-name">Priority Support</td>
                <td>—</td>
                <td>—</td>
                <td className="featured-column"><span className="check">✓</span></td>
                <td><span className="check">✓</span></td>
              </tr>
              <tr>
                <td className="feature-name">Dedicated Account Manager</td>
                <td>—</td>
                <td>—</td>
                <td className="featured-column">—</td>
                <td><span className="check">✓</span></td>
              </tr>
              <tr>
                <td className="feature-name">Custom Integrations</td>
                <td>—</td>
                <td>—</td>
                <td className="featured-column">—</td>
                <td><span className="check">✓</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="pricing-faq">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-grid">
          <div className="faq-item">
            <div className="faq-icon">💡</div>
            <h3>Can I change my plan later?</h3>
            <p>
              Yes! You can upgrade or downgrade your plan at any time from your account
              settings. Changes take effect immediately.
            </p>
          </div>
          <div className="faq-item">
            <div className="faq-icon">📊</div>
            <h3>What happens if I exceed my limits?</h3>
            <p>
              You'll be prompted to upgrade your plan when you reach your project or user
              limits. Your existing work is always safe.
            </p>
          </div>
          <div className="faq-item">
            <div className="faq-icon">🎯</div>
            <h3>Is there a free trial?</h3>
            <p>
              Yes! All plans include a 2-week free trial with full platform access. You won't be charged until after your trial ends. Cancel anytime during the trial with no charges. Click "Start Free Trial" on any plan above to get started.
            </p>
          </div>
          <div className="faq-item">
            <div className="faq-icon">🔄</div>
            <h3>Can I cancel anytime?</h3>
            <p>
              Yes, you can cancel your subscription at any time. Your access will continue
              until the end of your billing period.
            </p>
          </div>
          <div className="faq-item">
            <div className="faq-icon">💳</div>
            <h3>What payment methods do you accept?</h3>
            <p>
              We accept all major credit cards, debit cards, and digital wallets through our secure Stripe payment processor.
            </p>
          </div>
          <div className="faq-item">
            <div className="faq-icon">🛡️</div>
            <h3>Is my data secure?</h3>
            <p>
              Absolutely. We use bank-level encryption and security measures to protect your data. Your information is never shared with third parties.
            </p>
          </div>
        </div>
      </div>

      <div className="pricing-cta">
        <h2>Still have questions?</h2>
        <p>Our team is here to help you choose the right plan for your business.</p>
        <button className="cta-button" onClick={() => navigate('/contact')}>
          Contact Sales
        </button>
      </div>
    </div>
    </>
  );
};

export default PricingPage;
